import { useEffect, useMemo, useState } from "react";
import { FieldSkeleton } from "./FieldSkeleton";
import { getSelectStyles } from "./select-styling";
import AsyncSelect, { AsyncProps } from "react-select/async";
import _ from "lodash";

type OwnProps = {
  innerRef?: any;
  errorMessage?: string;
  label?: string;
  helperText?: string;
  width?: string;
  onChange?: (value: any) => void;
  retrieveAsyncOptions: (
    inputValue: string
  ) => Promise<
    {
      label: string;
      value: string | number;
    }[]
  >;
} & AsyncProps<any, any, any>;

export const AsyncSelectField = ({
  innerRef,
  errorMessage,
  label,
  helperText,
  value,
  width,
  retrieveAsyncOptions,
  onChange,
  ...rest
}: OwnProps) => {
  const [value_, setValue_] = useState<{
    value: string | number;
    label: string;
  } | null>(null);

  const debouncedLoadOptions = useMemo(() => {
    const fn = _.debounce(
      (
        inputValue: string,
        callback: (options: { label: string; value: string | number }[]) => void
      ) => {
        retrieveAsyncOptions(inputValue).then((options) => {
          callback(options);
        });
      },
      500
    );

    return fn;
  }, [retrieveAsyncOptions]); // Add retrieveAsyncOptions only if it's stable or memoized

  useEffect(() => {
    if (!value) {
      return;
    }
    if (value_) {
      return;
    }
    const setDefaultValue = async () => {
      const options = await retrieveAsyncOptions("");
      const option = options.find((o) => o.value === value);
      if (option) {
        setValue_(option);
      }
    };
    setDefaultValue();
    // We don't want to have retrieveAsyncOptions in the dependency array, as it has no say in when useEffect should fire
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    return () => {
      debouncedLoadOptions.cancel();
    };
  }, [debouncedLoadOptions]);

  return (
    <FieldSkeleton
      errorMessage={errorMessage}
      helperText={helperText}
      label={label}
      width={width}
    >
      <AsyncSelect
        ref={innerRef}
        menuPosition="fixed"
        value={value_}
        styles={getSelectStyles({ isMulti: rest.isMulti, errorMessage })}
        onChange={(value) => {
          const newValue = value as { value: string | number; label: string };
          setValue_(newValue);
          if (onChange) {
            onChange(newValue);
          }
        }}
        defaultOptions={true}
        loadOptions={(inputValue, callback) => {
          debouncedLoadOptions.cancel();
          if (inputValue === "") {
            retrieveAsyncOptions("").then(callback); // don't debounce
          } else {
            debouncedLoadOptions(inputValue, callback); // debounce for user input
          }
        }}
        {...rest}
      />
    </FieldSkeleton>
  );
};
